import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import MessageBox from './MessageBox';
import ChatDateSeparator from './ChatDateSeparator';
import { EmptyState } from '../../../../components/ui';
import useInterveneTheme from '../../hooks/useInterveneTheme';
import useMessageFeedStyles from '../../hooks/useMessageFeedStyles';
import { getMessageKey } from '../../utils/getMessageKey';
import { groupMessages } from '../../utils/groupMessages';
import { getMessageDateKey } from '../../utils/messageDateUtils';
import { getMessageTimestampMs } from '../../utils/normalizeMessageTimestamp';
import { chatDebug, summarizeMessages } from '../../utils/chatDebug';
import {
  getConversationDisplayName,
  getConversationPhone,
} from '../../utils/conversationIdentity';

function isOutboundMessage(message) {
  const origin = `${message?.origin || ''}`.toUpperCase();
  if (origin === 'OUTBOUND') return true;
  if (origin === 'INBOUND') return false;
  const direction = `${message?.direction || ''}`.toLowerCase();
  if (direction === 'outbound') return true;
  if (direction === 'inbound') return false;
  return Boolean(
    message?.chat_type === 'human_agent' ||
    message?.intervened_by
  );
}

export default function MessageFeed({
  scrollRef,
  onScroll,
  messages,
  activeUser,
  isLoadingMessages,
  isLoadingOlder,
  hasMoreMessages,
}) {
  const feedSx = useMessageFeedStyles();
  const { chat } = useInterveneTheme();
  const userName = getConversationDisplayName(activeUser, 'User');
  const activePhone = getConversationPhone(activeUser);

  const getSenderName = (message, isMine) => {
    if (isMine) return 'You';

    return (
      message?.whatsapp_name ||
      message?.sender_name ||
      message?.username ||
      userName
    );
  };

  const groupedMessages = React.useMemo(() => groupMessages(messages || []), [messages]);

  React.useEffect(() => {
    const messagesWithDifferentPhone = (messages || []).filter(
      (message) =>
        message?.phone_number &&
        activeUser?.phone_number &&
        `${message.phone_number}` !== `${activeUser.phone_number}`
    );

    chatDebug(
      messagesWithDifferentPhone.length
        ? 'message-feed-render-mismatch'
        : 'message-feed-render',
      {
        activeUser: {
          phone_number: activeUser?.phone_number,
          whatsapp_name: activeUser?.whatsapp_name,
        },
        count: messages?.length || 0,
        mismatchedCount: messagesWithDifferentPhone.length,
        mismatchedSample: summarizeMessages(messagesWithDifferentPhone),
        sample: summarizeMessages(messages),
        displaySample: (messages || []).slice(0, 5).map((message, index) => {
          const isMine = isOutboundMessage(message);
          const messageKey = getMessageKey(message, index);
          return {
            key: `${activePhone || 'chat'}:${messageKey}`,
            id: messageKey,
            origin: message.origin,
            messagePhone: message.phone_number,
            messageWhatsappName: message.whatsapp_name,
            senderName: getSenderName(message, isMine),
            text:
              message.content?.body?.text ||
              message.content?.text ||
              message.text ||
              message.message ||
              null,
          };
        }),
      },
      messagesWithDifferentPhone.length ? 'warn' : 'debug'
    );
  }, [activeUser?.phone_number, activeUser?.whatsapp_name, messages]);

  return (
    <Box
      ref={scrollRef}
      onScroll={onScroll}
      sx={{ ...feedSx, maxHeight: { xs: 'calc(100vh - 50px)', sm: 'auto' } }}
      role="log"
      aria-live="polite"
      aria-relevant="additions"
      aria-label="Conversation messages"
    >
      {isLoadingOlder && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={20} sx={{ color: chat.primary }} />
        </Box>
      )}

      {!isLoadingOlder && hasMoreMessages && messages.length > 0 && (
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ textAlign: 'center', py: 0.5, mb: 0.5 }}
        >
          Scroll up for older messages
        </Typography>
      )}

      {isLoadingMessages ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress size={28} sx={{ color: chat.primary }} />
        </Box>
      ) : messages.length === 0 ? (
        <EmptyState
          title="No messages yet"
          description="Start the conversation with this customer."
          sx={{ flex: 1, justifyContent: 'center' }}
        />
      ) : (
        groupedMessages.map(
          ({ message, isFirstInGroup, isLastInGroup, isGrouped }, index) => {
            const isMine = isOutboundMessage(message);
            const messageKey = getMessageKey(message, index);
            const dateKey = getMessageDateKey(message);
            const previousDateKey =
              index > 0 ? getMessageDateKey(groupedMessages[index - 1]?.message) : null;
            const showDateSeparator = dateKey && dateKey !== previousDateKey;
            const separatorTimestamp = getMessageTimestampMs(message);

            return (
              <React.Fragment key={`${activePhone || 'chat'}:${messageKey}`}>
                {showDateSeparator && separatorTimestamp != null && (
                  <ChatDateSeparator dateValue={separatorTimestamp} />
                )}
                <MessageBox
                  message={message}
                  isMine={isMine}
                  senderName={getSenderName(message, isMine)}
                  senderAvatar={isMine ? null : activeUser.profile_pic}
                  isFirstInGroup={isFirstInGroup}
                  isLastInGroup={isLastInGroup}
                  isGrouped={isGrouped}
                />
              </React.Fragment>
            );
          }
        )
      )}
    </Box>
  );
}
